var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ButtonGroup, Divider, FormControlLabel, ToggleButtonGroup, Tooltip } from '@mui/material';
import React from 'react';
import Button from '../../Button';
import Checkbox from '../../Checkbox/Checkbox';
import Chip from '../../Chip';
import DatePicker from '../../DatePicker';
import DateTimePicker from '../../DateTimePicker';
import Select from '../../Select/Select';
import SelectFormInput from '../../SelectFormInput/SelectFormInput';
import Switch from '../../Switch/Switch';
import TextField from '../../TextField';
import TextFormInput from '../../TextFormInput/TextFormInput';
import TimePickerFormInput from '../../TimePickerFormInput';
import ToggleButton from '../../ToggleButton/ToggleButton';
import TopNavItem from '../../TopNavItem';
import { TransformButtonGroup, TransformCheckbox, TransformChip, TransformDropdownButton, TransformFormField, TransformSelect, TransformSeparator, TransformTextField, TransformToggleButton, TransformTopNavItem, TransformedButton, } from './transformUtils';
import { isDropdownButton, isStyledComponent } from './utils';
/**
 * Transforms the specified item's element into the menu item component understood by the dropdown menu.
 *
 * @param item The wrapper containing an element to transform.
 * @param elType Convenient argument to specify directly the element type.
 * @param isWrapped Determines whether the specified component is wrapped in another component (typically Tooltip or `styled` component).
 * @returns The transformed item's element or `null` if unsupported element is detected.
 */
export function TransformToMenuItem(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var item = _a.item, options = _a.options, elType = _a.elType, isWrapped = _a.isWrapped, index = _a.index;
    var handleClose = options.handleClose;
    var key = 'K' || ((_b = item.el) === null || _b === void 0 ? void 0 : _b.key) || (item === null || item === void 0 ? void 0 : item.label) || ((_d = (_c = item.el) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.label) || 'ag-item-' + index;
    var retEl;
    switch (elType || item.el.type) {
        case Button:
            retEl = React.createElement(TransformedButton, __assign({}, { key: key, item: item, handleClose: handleClose, isWrapped: isWrapped }));
            break;
        case Checkbox:
        case FormControlLabel: // The 'FormControl' component can be either Checkbox or Switch.
        case Switch:
            retEl = React.createElement(TransformCheckbox, { item: item, key: key });
            break;
        case Chip:
            retEl = React.createElement(TransformChip, { item: item, key: key, isWrapped: isWrapped });
            break;
        case Divider:
            retEl = React.createElement(TransformSeparator, { item: item, key: key, index: index });
            break;
        case TextField:
            retEl = React.createElement(TransformTextField, { item: item, key: key });
            break;
        case TopNavItem:
            retEl = React.createElement(TransformTopNavItem, __assign({}, { item: item, handleClose: handleClose, isWrapped: isWrapped }));
            break;
        case DatePicker:
        case DateTimePicker:
            retEl = React.createElement(TransformFormField, { item: item, key: key, isDatePicker: true });
            break;
        case TimePickerFormInput:
        case TextFormInput:
            retEl = React.createElement(TransformFormField, { item: item, key: key });
            break;
        case Select:
        case SelectFormInput:
            retEl = React.createElement(TransformSelect, __assign({}, { key: key, item: item }));
            break;
        case ToggleButton:
            retEl = React.createElement(TransformToggleButton, { item: item, key: key });
            break;
        case ButtonGroup:
        case ToggleButtonGroup:
            var isToggleGroup = (elType || item.el.type) === ToggleButtonGroup;
            retEl = React.createElement(TransformButtonGroup, __assign({}, { key: key, item: item, handleClose: handleClose, isToggleGroup: isToggleGroup }));
            break;
        case Tooltip:
            // Special case - components might be wrapped in a tooltip component,
            // therefore we need to pass this info to the transformation functions.
            retEl = (React.createElement(TransformToMenuItem, { item: item, options: options, elType: item.el.props.children.type, isWrapped: true }));
            break;
        default:
            retEl = null;
    }
    // Handle special cases, like dropdown buttons and/or styled components.
    if (!retEl) {
        if (isDropdownButton((_f = (_e = item.el) === null || _e === void 0 ? void 0 : _e.props) === null || _f === void 0 ? void 0 : _f.children)) {
            retEl = React.createElement(TransformDropdownButton, { item: item, key: key, handleClose: handleClose });
        }
        else if (isStyledComponent((_g = item.el) === null || _g === void 0 ? void 0 : _g.type)) {
            retEl = (React.createElement(TransformToMenuItem, { item: item, options: options, elType: (_j = (_h = item.el) === null || _h === void 0 ? void 0 : _h.type) === null || _j === void 0 ? void 0 : _j.__emotion_base }));
        }
    }
    // This should never happen, but just in case...
    if (!retEl) {
        // eslint-disable-next-line no-console
        console.warn('[YDS-REACT] - ActionGroup: Unsupported element type found!', item, elType, item.el.type, 'resolved:', elType || item.el.type);
    }
    return retEl;
}
